import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useRbac } from "@/hooks/useRbac";
import { ROLE_LABELS, normalizeRole } from "@/lib/rbac";
import { getModalityChartColor } from "@/lib/modalities";
import {
  formatRangeLabel,
  formatSigned,
  getCurrentIsoWeekRange,
  getIsoWeekNumber,
  normalizeWeeklySummary,
} from "@/lib/weeklyDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_SEGMENTS = [
  { key: "0_7", label: "0-7D", color: "#D4D4D4" },
  { key: "8_14", label: "8-14D", color: "#6B6B6B" },
  { key: "15_30", label: "15-30D", color: "#F5A68A" },
  { key: "30_plus", label: "30D+", color: "var(--xl-primary)" },
];

const DeltaPill = ({ value, pts = false }) => {
  const n = Number(value) || 0;
  const positive = n > 0;
  const negative = n < 0;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
        positive
          ? "bg-[var(--xl-success-bg)] text-[var(--xl-success)]"
          : negative
            ? "bg-[var(--xl-primary-soft)] text-[var(--xl-primary)]"
            : "bg-secondary text-muted-foreground"
      }`}
    >
      {formatSigned(n, { pts })}
    </span>
  );
};

const KpiCard = ({ label, value, previous, delta, suffix = "", emphasize = false, loading }) => (
  <Card
    className={`border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${
      emphasize ? "border-t-[3px] border-t-[var(--xl-primary)]" : ""
    }`}
  >
    <CardContent className="p-3 sm:p-3.5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight sm:text-3xl">
        {loading ? "—" : `${value}${suffix}`}
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <DeltaPill value={delta} pts={suffix === "%"} />
        <span>
          vs {loading ? "—" : `${previous}${suffix}`}
        </span>
      </div>
    </CardContent>
  </Card>
);

const ModalityDots = ({ presentKeys = [], modalities = [] }) => {
  const present = new Set((presentKeys ?? []).map((k) => String(k)));
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {modalities.map((m) => {
        const key = String(m?.key ?? "");
        const on = present.has(key);
        const color = getModalityChartColor(key);
        return (
          <span
            key={key}
            title={`${m.label}${on ? " (uploaded)" : " (missing)"}`}
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={
              on
                ? { backgroundColor: color, boxShadow: `0 0 0 1px ${color}` }
                : { backgroundColor: "transparent", border: `1.5px solid ${color}`, opacity: 0.55 }
            }
          />
        );
      })}
    </div>
  );
};

export default function WeeklyDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { role } = useRbac();
  const isSuperAdmin = normalizeRole(role) === "super_admin";
  const initialRange = useMemo(() => getCurrentIsoWeekRange(), []);
  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialRange.dateTo);
  const [summary, setSummary] = useState(() => normalizeWeeklySummary());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!dateFrom || !dateTo) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/dashboard/summary`, {
          params: { date_from: dateFrom, date_to: dateTo },
        });
        const payload = res?.data?.data ?? res?.data ?? {};
        if (!cancelled) setSummary(normalizeWeeklySummary(payload));
      } catch (err) {
        console.error("Failed to load weekly dashboard summary", err);
        if (!cancelled) {
          setSummary(normalizeWeeklySummary());
          setError(err?.response?.data?.detail || "Failed to load weekly summary");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo]);

  const kpis = summary?.kpis ?? {};
  const cases = kpis.cases ?? { value: 0, previous: 0, delta: 0 };
  const datasets = kpis.datasets_uploaded ?? { value: 0, previous: 0, delta: 0 };
  const pending = kpis.pending_datasets ?? { value: 0, previous: 0, delta: 0 };
  const completeness = kpis.completeness ?? { value: 0, previous: 0, delta: 0 };
  const fullCompleteness = kpis.full_completeness ?? { value: 0, previous: 0, delta: 0 };

  const weekNumber = summary?.week_number ?? getIsoWeekNumber(dateFrom);
  const rangeLabel = formatRangeLabel(dateFrom, dateTo);
  const displayName = summary?.user?.name || user?.name || "—";
  const roleLabel =
    summary?.user?.role_label
    || ROLE_LABELS[user?.role]
    || user?.role
    || null;
  const territory = summary?.user?.territory || user?.territory || null;
  const userLine = [displayName, roleLabel, territory ? `${territory} territory` : null]
    .filter(Boolean)
    .join(" · ");

  const chase = summary?.chase ?? [];
  const pendingByAge = summary?.pending_by_age ?? {};
  const ageTotal = AGE_SEGMENTS.reduce((sum, seg) => sum + (pendingByAge[seg.key] || 0), 0) || 1;
  const reps = summary?.rankings?.reps ?? [];
  const hospitals = summary?.rankings?.hospitals ?? [];
  const zone = summary?.rankings?.zone || summary?.user?.zone || "your zone";
  const caseShare = summary?.case_share ?? { medtronic: 0, competition: 0, medtronic_pct: 0, delta_pts: 0 };
  const modalities = summary?.modalities ?? [];

  const moreThanLast = cases.delta;
  const headlineDelta =
    moreThanLast > 0
      ? `${moreThanLast} more than last`
      : moreThanLast < 0
        ? `${Math.abs(moreThanLast)} fewer than last`
        : "same as last period";

  const handleViewAll = () => {
    navigate("/patients?status=pending");
  };

  return (
    <div className="flex flex-col gap-3 pb-2" data-testid="weekly-dashboard">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--xl-primary)]">
            {weekNumber != null ? `Week ${weekNumber}` : "Date range"}
            {rangeLabel ? ` · ${rangeLabel}` : ""}
          </p>
          <h2 className="text-lg font-bold tracking-tight text-[var(--xl-text)] sm:text-xl lg:leading-snug">
            {loading ? (
              "Loading summary…"
            ) : (
              <>
                {cases.value} case{cases.value === 1 ? "" : "s"} this period, {headlineDelta}.{" "}
                {pending.value} dataset{pending.value === 1 ? "" : "s"} still missing
                <span className="text-[var(--xl-primary)]">.</span>
              </>
            )}
          </h2>
          <p className="text-xs text-muted-foreground sm:text-sm">{userLine}</p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Calendar className="h-3.5 w-3.5 shrink-0 text-[var(--xl-mut2)]" aria-hidden />
          <Input
            data-testid="weekly-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            max={dateTo || undefined}
            className="h-9 w-[9.5rem] border-[var(--xl-line2)] bg-white text-sm sm:w-36"
            aria-label="From date"
          />
          <span className="text-xs text-[var(--xl-mut)]">to</span>
          <Input
            data-testid="weekly-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            min={dateFrom || undefined}
            className="h-9 w-[9.5rem] border-[var(--xl-line2)] bg-white text-sm sm:w-36"
            aria-label="To date"
          />
        </div>
      </div>

      {error ? (
        <p className="rounded-xl border border-[var(--xl-primary-border)] bg-[var(--xl-primary-soft)] px-3 py-2 text-sm text-[var(--xl-primary)]">
          {error}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <KpiCard
          label="Cases"
          value={cases.value}
          previous={cases.previous}
          delta={cases.delta}
          loading={loading}
        />
        <KpiCard
          label="Datasets uploaded"
          value={datasets.value}
          previous={datasets.previous}
          delta={datasets.delta}
          loading={loading}
        />
        <KpiCard
          label="Pending datasets"
          value={pending.value}
          previous={pending.previous}
          delta={pending.delta}
          emphasize
          loading={loading}
        />
        <KpiCard
          label="CT Report"
          value={completeness.value}
          previous={completeness.previous}
          delta={completeness.delta}
          suffix="%"
          loading={loading}
        />
        <KpiCard
          label="All cases"
          value={fullCompleteness.value}
          previous={fullCompleteness.previous}
          delta={fullCompleteness.delta}
          suffix="%"
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-3">
        <Card className="flex h-full min-h-0 flex-col border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] lg:col-span-2">
          <CardHeader className="flex shrink-0 flex-row items-start justify-between gap-3 space-y-0 px-4 pb-1.5 pt-3 sm:px-5">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-sm font-bold sm:text-base">Chase this week</CardTitle>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Cases missing a dataset, oldest first
              </p>
            </div>
            <button
              type="button"
              onClick={handleViewAll}
              className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-[var(--xl-primary)] hover:underline"
              data-testid="weekly-chase-view-all"
            >
              View all
            </button>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col px-0 pb-0 pt-0">
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading ? (
                <p className="px-5 py-4 text-center text-sm text-muted-foreground">Loading…</p>
              ) : chase.length === 0 ? (
                <p className="px-5 py-4 text-center text-sm text-muted-foreground">
                  No pending datasets in this range
                </p>
              ) : (
                <ul className="divide-y divide-[var(--xl-line)]">
                  {chase.slice(0, 8).map((item) => (
                    <li
                      key={item.id ?? `${item.case_id}-${item.patient_uhid}`}
                      className="flex flex-col gap-2 px-4 py-2 sm:flex-row sm:items-center sm:gap-3 sm:px-5"
                    >
                      <span className="inline-flex w-fit shrink-0 items-center justify-center rounded-md bg-[var(--xl-primary-soft)] px-2 py-0.5 text-[11px] font-bold tabular-nums text-[var(--xl-primary)]">
                        {item.days_pending} DAYS
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">
                          Case #{item.case_id}
                          {item.patient_name ? (
                            <>
                              <span className="text-muted-foreground"> · </span>
                              {item.patient_name}
                            </>
                          ) : null}
                        </p>
                        {item.hospital_name || item.doctor_name ? (
                          <p className="truncate text-xs text-muted-foreground">
                            {[item.hospital_name, item.doctor_name].filter(Boolean).join(" · ")}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                        <ModalityDots presentKeys={item.modalities_present} modalities={modalities} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {modalities.length > 0 ? (
              <div className="flex shrink-0 flex-wrap gap-x-3 gap-y-1 border-t border-[var(--xl-line)] px-4 py-2 sm:px-5">
                {modalities.map((m) => (
                  <span key={m.key} className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: getModalityChartColor(m.key) }}
                      aria-hidden
                    />
                    {m.label}
                  </span>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="flex h-full flex-col gap-3">
          <Card className="border-0 bg-[var(--xl-black)] text-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <CardContent className="flex flex-col justify-between gap-3 p-3.5 sm:p-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--xl-primary)]">
                  Case share
                </p>
                <div className="mt-1.5 flex flex-wrap items-end gap-2">
                  <p className="text-3xl font-bold tabular-nums tracking-tight sm:text-4xl">
                    {loading ? "—" : `${caseShare.medtronic_pct}%`}
                  </p>
                  <span className="mb-0.5 inline-flex rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white/80">
                    {formatSigned(caseShare.delta_pts, { pts: true })} WoW
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex h-2 overflow-hidden rounded-full bg-white/15">
                  <div
                    className="h-full bg-[var(--xl-primary)]"
                    style={{ width: `${Math.min(100, Math.max(0, caseShare.medtronic_pct))}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-white/80">
                  <span>{caseShare.medtronic} {caseShare.own_org_name || "Own"}</span>
                  <span>{caseShare.competition} Competition</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <CardHeader className="space-y-0.5 px-3.5 pb-1.5 pt-3 sm:px-4">
              <CardTitle className="text-sm font-bold sm:text-base">Pending by age</CardTitle>
              <p className="text-xs text-muted-foreground">Days since the case was created</p>
            </CardHeader>
            <CardContent className="space-y-2.5 px-3.5 pb-3.5 sm:px-4">
              <div className="flex h-2 overflow-hidden rounded-full bg-secondary">
                {AGE_SEGMENTS.map((seg) => {
                  const count = pendingByAge[seg.key] || 0;
                  const pct = (count / ageTotal) * 100;
                  if (!count) return null;
                  return (
                    <div
                      key={seg.key}
                      className="h-full"
                      style={{ width: `${pct}%`, backgroundColor: seg.color }}
                      title={`${seg.label}: ${count}`}
                    />
                  );
                })}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {AGE_SEGMENTS.map((seg) => (
                  <div key={seg.key} className="text-center">
                    <p className="text-base font-bold tabular-nums sm:text-lg">{loading ? "—" : pendingByAge[seg.key] || 0}</p>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {seg.label}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div
        className={`grid grid-cols-1 items-stretch gap-3 ${isSuperAdmin ? "xl:grid-cols-2" : ""}`}
      >
        <Card
          className="shrink-0 border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
          data-testid="weekly-leaderboard"
        >
          <CardHeader className="space-y-0.5 px-4 pb-1.5 pt-3 sm:px-5">
            <CardTitle className="text-sm font-bold sm:text-base">
              Your rank in {zone}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Among company representatives · selected dates
            </p>
          </CardHeader>
          <CardContent className="px-0 pb-2 pt-0">
            {loading ? (
              <p className="px-5 py-4 text-center text-sm text-muted-foreground">Loading…</p>
            ) : reps.length === 0 ? (
              <p className="px-5 py-4 text-center text-sm text-muted-foreground">No ranking data</p>
            ) : (
              <div className="max-h-[18rem] min-h-[12rem] overflow-auto sm:max-h-[22rem] sm:min-h-[14rem]">
                <table className="w-full min-w-[560px] text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr className="border-b border-[var(--xl-line)] text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      <th className="px-4 py-2 sm:px-5">#</th>
                      <th className="px-2 py-2">Representative</th>
                      <th className="px-2 py-2">Cases</th>
                      <th className="px-2 py-2">Completeness</th>
                      <th className="px-4 py-2 text-right sm:px-5">Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reps.map((row) => (
                      <tr
                        key={row.id ?? `${row.rank}-${row.name}`}
                        className={`border-b border-[var(--xl-line)] last:border-0 ${
                          row.is_current_user ? "bg-[var(--xl-primary-soft)]/60" : ""
                        }`}
                      >
                        <td className="px-4 py-2 tabular-nums text-muted-foreground sm:px-5">
                          {row.rank}
                        </td>
                        <td className="px-2 py-2">
                          <p className="font-semibold">{row.name}</p>
                          {row.territory && row.territory !== "—" ? (
                            <p className="text-xs text-muted-foreground">{row.territory}</p>
                          ) : null}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold tabular-nums">{row.cases}</span>
                            <DeltaPill value={row.cases_wow} />
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex min-w-[8rem] items-center gap-2">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${Math.min(100, Math.max(0, row.completeness))}%`,
                                  backgroundColor:
                                    row.completeness < 70 ? "var(--xl-primary)" : "var(--xl-black)",
                                }}
                              />
                            </div>
                            <span className="w-10 shrink-0 text-right text-xs font-semibold tabular-nums">
                              {row.completeness}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right font-semibold tabular-nums sm:px-5">
                          {row.pending}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {isSuperAdmin ? (
          <Card
            className="shrink-0 border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            data-testid="weekly-hospital-leaderboard"
          >
            <CardHeader className="space-y-0.5 px-4 pb-1.5 pt-3 sm:px-5">
              <CardTitle className="text-sm font-bold sm:text-base">
                Hospital leaderboard
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Hospitals by cases · selected dates
              </p>
            </CardHeader>
            <CardContent className="px-0 pb-2 pt-0">
              {loading ? (
                <p className="px-5 py-4 text-center text-sm text-muted-foreground">Loading…</p>
              ) : hospitals.length === 0 ? (
                <p className="px-5 py-4 text-center text-sm text-muted-foreground">No hospital ranking data</p>
              ) : (
                <div className="max-h-[18rem] min-h-[12rem] overflow-auto sm:max-h-[22rem] sm:min-h-[14rem]">
                  <table className="w-full min-w-[480px] text-left text-sm">
                    <thead className="sticky top-0 z-10 bg-white">
                      <tr className="border-b border-[var(--xl-line)] text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        <th className="px-4 py-2 sm:px-5">#</th>
                        <th className="px-2 py-2">Hospital</th>
                        <th className="px-2 py-2">Cases</th>
                        <th className="px-2 py-2">Completeness</th>
                        <th className="px-4 py-2 text-right sm:px-5">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hospitals.map((row) => (
                        <tr
                          key={row.id ?? `${row.rank}-${row.name}`}
                          className="border-b border-[var(--xl-line)] last:border-0"
                        >
                          <td className="px-4 py-2 tabular-nums text-muted-foreground sm:px-5">
                            {row.rank}
                          </td>
                          <td className="px-2 py-2">
                            <p className="font-semibold">{row.name}</p>
                          </td>
                          <td className="px-2 py-2">
                            <span className="font-semibold tabular-nums">{row.cases}</span>
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex min-w-[8rem] items-center gap-2">
                              <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${Math.min(100, Math.max(0, row.completeness))}%`,
                                    backgroundColor:
                                      row.completeness < 70 ? "var(--xl-primary)" : "var(--xl-black)",
                                  }}
                                />
                              </div>
                              <span className="w-10 shrink-0 text-right text-xs font-semibold tabular-nums">
                                {row.completeness}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right font-semibold tabular-nums sm:px-5">
                            {row.pending}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
